import React, { useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import createAxiosInstance from "../utility/utils";
import {
  Alert,
  Spinner,
  Container,
  Breadcrumb,
  Col,
  Row,
  Table,
  Button,
  Form,
  Pagination,
} from "react-bootstrap";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";

export default function MySessions() {
  const navigate = useNavigate();

  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(""); 

  // Search and pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchSubject, setSearchSubject] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const itemsPerPage = 10;

  const axiosInstance = createAxiosInstance(getToken);

const fetchSessions = async (subjectQuery, dateQuery) => {
  try {
    setError(""); 

    if (sessions.length === 0) {
      setIsLoading(true);
    } else {
      setIsSearching(true);
    }

    const response = await axiosInstance.get(
      `/api/teacher/${user.id}/sessions?page=${currentPage}&limit=${itemsPerPage}&subject=${subjectQuery}&date=${dateQuery}`
    );

    setSessions(response.data.sessions);
    setTotalPages(Math.ceil(response.data.total / itemsPerPage));
  } catch (err) {
    console.error("Error fetching sessions:", err);

    if (err.response?.status === 403) {
      setError("Access Denied: Only teachers can view this data.");
    } else {
      setError(err.response?.data?.error || "Failed to fetch sessions.");
    }
  } finally {
    setIsLoading(false);
    setIsSearching(false);
  }
};

  // Debounce with 500ms delay
  const debouncedSearch = useCallback(
    debounce((subject, date, currentPage) => {
      fetchSessions(subject, date, currentPage);
    }, 500),
    [currentPage, user?.id]
  );

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      debouncedSearch(searchSubject, searchDate, currentPage);
    }
    return () => debouncedSearch.cancel();
  }, [
    currentPage,
    searchSubject,
    searchDate,
    debouncedSearch,
    isLoaded,
    isSignedIn,
  ]);

  const handleClear = () => {
    setSearchSubject("");
    setSearchDate("");
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSessionClick = async (session) => {
    try {
      const response = await axiosInstance.get(
        `/api/attendance/session/${session.sessionId}`
      );
      setSelectedSession(session.sessionId);
      setAttendanceData(response.data.attendanceRecords);

      // navigate to the next page
      navigate("/previous-attendance", {
        state: {
          batchName: session.batch.name,
          subjectName: session.subject.name,
          date: session.date,
          time: session.time,
          sessionId: session.sessionId,
          attendanceRecords: response.data.attendanceRecords,
        },
      });
    } catch (err) {
      console.log(err);
    }
  };

  // const temp = (
  //   <Container className="mt-4">
  //     <Breadcrumb>
  //       <Breadcrumb.Item href="/">Apps</Breadcrumb.Item>
  //       <Breadcrumb.Item href="/batches">Attendance</Breadcrumb.Item>
  //       <Breadcrumb.Item active>Attendance History</Breadcrumb.Item>

  //     </Breadcrumb>

  //     <Row className="flex-column flex-md-row">
  //       <Col md={4} className="mb-4 mb-md-0">
  //         <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
  //           <div className="list-group">
  //             {sessions.map(session => (
  //               <button
  //                 key={session.sessionId}
  //                 className="list-group-item list-group-item-action text-left"
  //                 onClick={() => handleSessionClick(session.sessionId)}
  //               >
  //                 <div className="font-semibold">{session.batch.name}</div>
  //                 <div className="text-sm text-gray-600">
  //                   {session.subject.name} - {session.date} {session.time}
  //                 </div>
  //               </button>
  //             ))}
  //           </div>
  //         </div>
  //       </Col>

  //       {/* <Col md={8}>
  //           {selectedSession && (
  //             <div className="mt-4 mt-md-0">
  //               <h3 className="mb-3">Attendance for Session: {selectedSession}</h3>
  //               <div className="overflow-auto">
  //                 <Table striped bordered hover responsive>
  //                   <thead className="bg-gray-200">
  //                     <tr>
  //                       <th>Student Name</th>
  //                       <th>USN</th>
  //                       <th>Attendance Status</th>
  //                     </tr>
  //                   </thead>
  //                   <tbody>
  //                     {attendanceData.map(record => (
  //                       <tr key={record.student._id}>
  //                         <td>{record.student.name}</td>
  //                         <td>{record.student.usnNumber}</td>
  //                         <td>{record.attendanceStatus}</td>
  //                       </tr>
  //                     ))}
  //                   </tbody>
  //                 </Table>
  //               </div>
  //             </div>
  //           )}
  //         </Col> */}
  //     </Row>
  //   </Container>
  // );

  return (
    <Container className="mt-4">
      <Breadcrumb>
    
        <Breadcrumb.Item href="/">Apps</Breadcrumb.Item>
        <Breadcrumb.Item href="/batches">Attendance</Breadcrumb.Item>
        <Breadcrumb.Item href="/teachers/mySession">Sessions</Breadcrumb.Item>
        <Breadcrumb.Item active> History</Breadcrumb.Item>
      </Breadcrumb>
      {error && (
  <Alert variant="danger" onClose={() => setError("")} dismissible>
    {error}
  </Alert>
)}

      {/* Search Filters */}
      <Row className="mb-3 align-items-end g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Subject Name</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search by subject..."
                value={searchSubject}
                onChange={(e) => {
                  setSearchSubject(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
              />
              {(isLoading || isSearching) && (
                <Spinner className="position-absolute top-50 end-0 translate-middle-y me-2 spinner-border spinner-border-sm opacity-75" />
              )}
            </div>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={searchDate}
              onChange={(e) => {
                setSearchDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Button
            variant="outline-secondary"
            onClick={handleClear}
            className="w-100"
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        <Container className="d-flex justify-content-center align-items-center min-vh-100">
          <Spinner animation="border" />
        </Container>
      ) : (
        <Row>
          <Col>
            {/* <div className="overflow-auto border rounded-lg shadow-sm" style={{ maxHeight: '70vh' }}> */}
            {sessions.length === 0 ? (
              <Alert variant="info" className="text-center">
                {isSearching ? "Searching..." : "No records found"}
              </Alert>
            ) : (
              <>
                <div className="border rounded-lg overflow-hidden shadow-sm">
                  <Table hover responsive className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Batch</th>
                        <th className="d-none d-md-table-cell">Subject</th>
                        <th className="d-none d-md-table-cell">Date</th>
                        <th>Time</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((session) => (
                        <tr key={session.sessionId}>
                          <td>{session.batch.name}</td>
                          <td className="d-none d-md-table-cell">
                            {session.subject.name}
                          </td>
                          <td className="d-none d-md-table-cell">
                            {session.date}
                          </td>
                          <td>{session.time}</td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleSessionClick(session)}
                              className="text-decoration-none"
                            >
                              <ExternalLink size={16} className="me-1" />
                              <span className="d-none d-md-inline">View</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <Pagination>
                      <Pagination.First
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      />

                      {Array.from({ length: totalPages }, (_, idx) => (
                        <Pagination.Item
                          key={idx + 1}
                          active={idx + 1 === currentPage}
                          onClick={() => handlePageChange(idx + 1)}
                        >
                          {idx + 1}
                        </Pagination.Item>
                      ))}

                      <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      />
                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
}
